import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { recordDonation } from '../services/api';

const CheckoutPage = ({ onNavigateBack, onComplete }) => {
  const { items, total, clearCart } = useCart();
  const [donorName, setDonorName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!donorName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare donation data
      const donationData = {
        donorName: donorName.trim(),
        activities: items.map(item => ({
          id: item.id,
          amount: item.amount,
        })),
        paymentMethod,
      };

      // Record donation via API
      await recordDonation(donationData);

      // Clear cart and navigate to thank you page
      clearCart();
      onComplete();
    } catch (err) {
      console.error('Failed to record donation:', err);
      setError('Failed to record donation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <button
            onClick={onNavigateBack}
            className="bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-dark transition-colors"
          >
            Back to Registry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onNavigateBack}
          className="text-primary hover:text-primary-dark mb-6 flex items-center gap-2"
        >
          ← Back to Registry
        </button>

        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Make it Happen</h1>

          {/* Order Summary */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Memories to be Gifted</h2>
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.cartItemId} className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">{item.name}</span>
                  <span className="font-semibold">€{item.amount}</span>
                </div>
              ))}
              <div className="flex justify-between items-center py-3 text-lg font-bold">
                <span>Total</span>
                <span className="text-primary">€{total}</span>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Donor Name */}
            <div>
              <label htmlFor="donor-name" className="block text-sm font-medium text-gray-700 mb-2">
                Your Name *
              </label>
              <input
                id="donor-name"
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="John & Jane Doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Payment Method *
              </label>
              <div className="space-y-3">
                <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="radio"
                    name="payment-method"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">PayPal</div>
                    <p className="text-sm text-gray-600 mb-2">
                      Send payment via PayPal.me link (Friends & Family)
                    </p>
                    {paymentMethod === 'paypal' && (
                      <div className="text-sm bg-gray-50 p-3 rounded space-y-2">
                        <a
                          href="https://paypal.me/bennxmeyer"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          paypal.me/bennxmeyer →
                        </a>
                        <p className="text-gray-600">Please select <strong>Friends & Family</strong> and add your name in the note so we know who it's from 💛</p>
                      </div>
                    )}
                  </div>
                </label>

                <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="radio"
                    name="payment-method"
                    value="bank_transfer"
                    checked={paymentMethod === 'bank_transfer'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">Bank Transfer</div>
                    <p className="text-sm text-gray-600 mb-2">
                      Direct bank transfer via IBAN
                    </p>
                    {paymentMethod === 'bank_transfer' && (
                      <div className="text-sm bg-gray-50 p-3 rounded">
                        <p className="font-mono">IBAN: DE95 2004 1144 0502 5382 00</p>
                        <p className="text-gray-600 mt-1">Name: Benjamin Meyer</p>
                        <p className="text-gray-600 mt-1">Bank: Comdirect</p>
                        <p className="text-gray-600 mt-2">Please add your name as the payment reference so we know who it's from 💛</p>
                      </div>
                    )}
                  </div>
                </label>

                <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="radio"
                    name="payment-method"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">Cash</div>
                    <p className="text-sm text-gray-600 mb-2">
                      We'll bring it in person 💶
                    </p>
                    {paymentMethod === 'cash' && (
                      <div className="text-sm bg-gray-50 p-3 rounded">
                        <p className="text-gray-600">We'll have a donation / card box at our wedding</p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
            {paymentMethod && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Important:</strong> Please complete your payment using the selected method above, 
                  then click "I have Paid" below to record your donation.
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-900">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !donorName.trim() || !paymentMethod}
              className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {isSubmitting ? 'Processing...' : 'I have Paid'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
